import FunnelHeader from "@components/quiz/FunnelHeader"
import FunnelLayout from "@components/quiz/FunnelLayout"
import StickyFooter from "@components/quiz/StickyFooter"
import Spinner from "@components/Spinner"
import StatusIcon from "@components/StatusIcon"
import {
  AuthEmailLink,
  authEmailLinkFor,
  verifyAuthEmailLinkOnce,
} from "@helpers/authEmailLink"
import { supabase } from "@helpers/supabase"
import { CheckIcon, ClockIcon, EnvelopeIcon } from "@heroicons/react/24/outline"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate } from "react-router-dom"

const ACCOUNT_PATH = "/account/subscription"
const SIGN_IN_PATH = "/account/sign-in"

type ConfirmEmailState =
  | "verifying"
  | "emailChanged"
  | "confirmed"
  | "pending"
  | "linkExpired"

/** What the link's result means, once it's known. */
function stateFor(type: string | undefined, hasSession: boolean) {
  if (!hasSession) {
    // NOTE: Only the first of the two links confirming an email change (one
    // to the old address, one to the new) verifies without a session.
    return type === "email_change" ? "pending" : "linkExpired"
  }
  return type === "email_change" ? "emailChanged" : "confirmed"
}

function initialState(link: AuthEmailLink): ConfirmEmailState {
  switch (link.kind) {
    case "tokenHash":
      return "verifying"
    case "session":
      return stateFor(link.type, true)
    case "pending":
      return "pending"
    default:
      return "linkExpired"
  }
}

/**
 * Where the links confirming an email address lead (an email change, which
 * the app asks for, or a sign-up), and magic links to sign in on the web.
 *
 * The new links carry a `token_hash`, verified here. The old ones went
 * through Supabase's verify endpoint and come back with the result in the
 * fragment (see AuthEmailLink).
 */
export default function ConfirmEmailPage() {
  const { pathname } = useLocation()
  const [link] = useState(() => authEmailLinkFor(pathname))
  const [state, setState] = useState<ConfirmEmailState>(() =>
    initialState(link),
  )

  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    if (link.kind !== "tokenHash") return
    if (!link.type) {
      setState("linkExpired")
      return
    }

    let isCurrent = true
    const type = link.type
    verifyAuthEmailLinkOnce(
      (params) => supabase.auth.verifyOtp(params),
      link.tokenHash,
      type,
    )
      .then(({ session, error }) => {
        if (!isCurrent) return
        if (error) {
          console.warn("Verifying the email link failed", error.code)
          setState("linkExpired")
        } else if (type === "magiclink" && session) {
          navigate(ACCOUNT_PATH, { replace: true })
        } else {
          setState(stateFor(type, Boolean(session)))
        }
      })
      .catch((error: unknown) => {
        console.error(error)
        if (isCurrent) setState("linkExpired")
      })

    return () => {
      isCurrent = false
    }
  }, [link, navigate])

  if (state === "verifying") {
    return (
      <FunnelLayout header={<FunnelHeader />}>
        <div
          aria-busy="true"
          className="flex grow flex-col items-center justify-center"
        >
          <Spinner />
        </div>
      </FunnelLayout>
    )
  }

  const content = {
    confirmed: { icon: CheckIcon, tone: "green" },
    emailChanged: { icon: CheckIcon, tone: "green" },
    linkExpired: { icon: ClockIcon, tone: "yellow" },
    pending: { icon: EnvelopeIcon, tone: "primary" },
  } as const

  const { icon, tone } = content[state]

  return (
    <FunnelLayout header={<FunnelHeader />}>
      <div
        aria-live="polite"
        className="flex grow animate-fade-up flex-col gap-6 pb-8 pt-6"
      >
        <StatusIcon icon={icon} tone={tone} />

        <div className="space-y-3">
          <h1 className="vm-heading">
            {t(`pages.account.confirmEmail.${state}.heading`)}
          </h1>

          <p className="vm-subheading">
            {t(`pages.account.confirmEmail.${state}.text`)}
          </p>
        </div>

        {state !== "pending" && (
          <StickyFooter>
            {state === "linkExpired" ? (
              <Link className="vm-button-secondary" to={SIGN_IN_PATH}>
                {t("pages.account.forgotPassword.backToSignIn")}
              </Link>
            ) : (
              <Link className="vm-button-primary" to={ACCOUNT_PATH}>
                {t("pages.account.confirmEmail.goToAccount")}
              </Link>
            )}
          </StickyFooter>
        )}
      </div>
    </FunnelLayout>
  )
}
